import React from 'react';
import { motion } from 'framer-motion';
import {
  FaGraduationCap,
  FaGlobe,
  FaUsers,
  FaAward,
  FaMusic,
  FaHeart,
  FaBookOpen,
  FaChalkboardTeacher,
  FaYoutube,
  FaInstagram,
  FaFacebook
} from 'react-icons/fa';

const fadeInUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6 } }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.2
    }
  }
};

const About = () => {
  const achievements = [
    {
      icon: <FaGraduationCap className="text-3xl text-gold" />,
      title: "Tabla Alankar & M.A. in Tabla",
      description: "Advanced qualifications in classical music"
    },
    {
      icon: <FaUsers className="text-3xl text-gold" />,
      title: "10+ Years Teaching Experience",
      description: "Since 2016, nurturing tabla enthusiasts worldwide"
    },
    {
      icon: <FaGlobe className="text-3xl text-gold" />,
      title: "Global Reach",
      description: "Students from USA, Canada, Australia & beyond"
    },
    {
      icon: <FaAward className="text-3xl text-gold" />,
      title: "Renowned Institutions",
      description: "Associated with institutions in India"
    }
  ];

  const gurus = [
    {
      name: "Shri Rajendra Koundade",
      location: "Nipani",
      description: "Highly respected guru whose traditional teachings form the foundation of Atharva's musical journey",
      image: "/images/rajendra_koundade.jpg"
    },
    {
      name: "Pandit Aniruddha Deshpande",
      location: "Pune",
      description: "Esteemed mentor whose guidance continues to inspire and shape Atharva's artistic expression",
      image: "/images/aniruddha_deshpande.jpg"
    }
  ];

  return (
    <section id="about" className="py-24 bg-cream dark:bg-maroon-dark relative overflow-hidden transition-colors duration-500">
      {/* Subtle decorative bg */}
      <div className="absolute inset-0 opacity-[0.03]">
        <div className="absolute top-20 left-20 w-40 h-40 bg-gold rounded-full blur-3xl" />
        <div className="absolute bottom-20 right-20 w-60 h-60 bg-gold rounded-full blur-3xl" />
      </div>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Section Frame */}
        <motion.div 
          className="section-frame"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
          variants={fadeInUp}
        >
          {/* Header */}
          <div className="text-center mb-16">
            <h2 className="font-serif text-4xl md:text-5xl font-bold text-maroon dark:text-gold-light mb-4 transition-colors duration-500">
              About <span className="text-gold">Taalsetu</span>
            </h2>
            <div className="gold-divider mb-6" />
            <p className="font-sans text-lg text-maroon/60 dark:text-gold-light/60 max-w-2xl mx-auto transition-colors duration-500">
              Where tradition meets modern learning
            </p>
          </div>

          {/* Main Story */}
          <div className="mb-20">
            <div className="grid md:grid-cols-2 gap-12 items-center">
              <motion.div variants={fadeInUp}>
                <div className="flex items-center mb-6">
                  <FaMusic className="text-3xl text-gold mr-4" />
                  <h3 className="font-serif text-3xl font-bold text-maroon dark:text-gold-light transition-colors duration-500">Our Story</h3>
                </div>
                <p className="font-sans text-base text-maroon/70 dark:text-gold-light/70 leading-relaxed mb-5 transition-colors duration-500">
                  <strong className="text-gold-dark dark:text-gold">Taalsetu</strong> is a dedicated online and offline platform committed to the teaching and promotion of Tabla, founded by <strong className="text-maroon dark:text-gold-light">Atharva Kagalkar</strong> — a passionate tabla artist and experienced educator.
                </p>
                <p className="font-sans text-base text-maroon/70 dark:text-gold-light/70 leading-relaxed mb-5 transition-colors duration-500">
                  Atharva began his journey in Tabla at a young age and has since earned his <strong>Tabla Alankar</strong> as well as a Master's degree (M.A.) in Tabla. He has received extensive training under the esteemed guidance of <strong>Shri Rajendra Koundade (Nipani)</strong> and <strong>Pandit Aniruddha Deshpande (Pune)</strong>, shaping his strong foundation in both traditional and contemporary styles.
                </p>
                <p className="font-sans text-base text-maroon/70 dark:text-gold-light/70 leading-relaxed transition-colors duration-500">
                  With over <strong className="text-gold-dark dark:text-gold">10 years of teaching experience since 2016</strong>, Atharva has successfully mentored students across the globe. Through Taalsetu, he continues to connect with learners from countries such as the USA, Canada, Australia, and beyond, making quality classical music education accessible worldwide.
                </p>
              </motion.div>

              <motion.div 
                className="relative"
                variants={fadeInUp}
              >
                <div className="bg-cream-dark dark:bg-white/5 rounded-2xl p-8 text-center border border-gold/20 transition-colors duration-500">
                  <div className="mb-6">
                    <img
                      src="/images/gallery-1.jpg"
                      alt="Atharva Kagalkar"
                      className="w-48 h-auto object-contain rounded-lg mx-auto mb-4 shadow-lg border-3 border-gold/30 bg-white hover:scale-105 transition-transform duration-500"
                      onError={(e) => {
                        e.currentTarget.style.display = 'none';
                        e.currentTarget.nextSibling.style.display = 'flex';
                      }}
                    />
                    <div className="w-36 h-36 bg-gradient-to-br from-gold to-gold-dark rounded-full mx-auto mb-4 shadow-lg items-center justify-center hidden">
                      <FaHeart className="text-5xl text-white" />
                    </div>
                    <h4 className="font-serif text-2xl font-bold text-maroon dark:text-gold-light mb-1 transition-colors duration-500">Atharva Kagalkar</h4>
                    <p className="text-gold font-semibold font-sans text-sm tracking-wider uppercase">Founder & Master Teacher</p>
                  </div>
                  <p className="font-serif text-maroon/60 dark:text-gold-light/60 italic text-lg transition-colors duration-500">
                    "Dedicated to preserving tradition while embracing modern teaching methods"
                  </p>
                </div>
              </motion.div>
            </div>
          </div>

          {/* Gold Divider */}
          <div className="gold-divider mb-16" />

          {/* Gurus Section */}
          <div className="mb-20">
            <div className="text-center mb-10">
              <h3 className="font-serif text-3xl font-bold text-maroon dark:text-gold-light mb-4 transition-colors duration-500">Guided by Masters</h3>
              <p className="font-sans text-base text-maroon/60 dark:text-gold-light/60 max-w-3xl mx-auto transition-colors duration-500">
                Atharva's musical journey has been enriched by the teachings of two highly respected gurus
              </p>
            </div>

            <motion.div 
              className="grid md:grid-cols-2 gap-8"
              variants={staggerContainer}
            >
              {gurus.map((guru, index) => (
                <motion.div
                  key={index}
                  variants={fadeInUp}
                  className="bg-white/60 dark:bg-white/5 rounded-xl p-8 border border-gold/15 hover:border-gold/30 transition-all duration-400 hover:shadow-lg group"
                >
                  {guru.image && (
                    <div className="overflow-hidden rounded-lg mb-6 shadow-md border-2 border-gold/20">
                      <img
                        src={guru.image}
                        alt={guru.name}
                        className="w-full h-auto max-h-[500px] object-contain block bg-black/5 group-hover:scale-105 transition-transform duration-700"
                      />
                    </div>
                  )}
                  <div className="w-12 h-0.5 bg-gold mb-5 group-hover:w-20 transition-all duration-400" />
                  <h4 className="font-serif text-xl font-bold text-maroon dark:text-gold-light mb-1 transition-colors duration-500">{guru.name}</h4>
                  <p className="text-gold font-semibold text-sm mb-4 tracking-wider">{guru.location}</p>
                  <p className="font-sans text-maroon/60 dark:text-gold-light/60 leading-relaxed transition-colors duration-500">{guru.description}</p>
                </motion.div>
              ))}
            </motion.div>
          </div>

          {/* Gold Divider */}
          <div className="gold-divider mb-16" />

          {/* Annual Event Section */}
          <div className="mb-20">
            <div className="text-center mb-10">
              <h3 className="font-serif text-3xl font-bold text-maroon dark:text-gold-light mb-4 transition-colors duration-500">The Annual Event of Taalsetu Academy</h3>
              <p className="font-sans text-xl text-gold-dark dark:text-gold font-semibold max-w-3xl mx-auto transition-colors duration-500">
                Avishkaar - A journey through beats
              </p>
            </div>

            {/* Newest edition runs full width; the two earlier ones sit side by
                side beneath it. The archive photos have very different aspect
                ratios (2024 is panoramic, 2025 is 3:2), so their image boxes are
                pinned to a fixed ratio and the captions pushed down with mt-auto
                — otherwise the two cards end up different heights. */}
            <motion.div
              className="grid md:grid-cols-2 gap-8 mb-10"
              variants={staggerContainer}
            >
              <motion.div variants={fadeInUp} className="md:col-span-2 bg-white/60 dark:bg-white/5 rounded-xl overflow-hidden shadow-lg border border-gold/20 hover:border-gold/40 hover:shadow-xl transition-all duration-400 group">
                <div className="relative overflow-hidden bg-black/5 flex items-center justify-center">
                  <img
                    src="/images/avishkaar_2026.jpg"
                    alt="Students and teachers of Taalsetu Music Academy on stage at Avishkaar 2026"
                    width="1600"
                    height="1066"
                    loading="lazy"
                    className="w-full h-auto max-h-[520px] object-contain object-center group-hover:scale-105 transition-transform duration-700"
                  />
                  <div className="absolute inset-0 bg-maroon/20 group-hover:bg-transparent transition-colors duration-500 pointer-events-none" />
                  <span className="absolute top-4 right-4 bg-gold text-maroon-dark font-sans text-xs font-bold tracking-widest uppercase px-3 py-1.5 rounded-full shadow-md">
                    Latest
                  </span>
                </div>
                <div className="p-4 text-center bg-maroon text-gold-light font-serif text-lg tracking-wider">
                  Avishkaar 2026
                </div>
              </motion.div>

              <motion.div variants={fadeInUp} className="flex flex-col h-full bg-white/60 dark:bg-white/5 rounded-xl overflow-hidden shadow-lg border border-gold/15 hover:border-gold/30 hover:shadow-xl transition-all duration-400 group">
                <div className="relative aspect-[16/9] overflow-hidden bg-black/5 flex items-center justify-center">
                  <img
                    src="/images/avishkaar_2025.jpg"
                    alt="Avishkaar 2024"
                    width="1024"
                    height="684"
                    loading="lazy"
                    className="w-full h-full object-contain object-center group-hover:scale-110 transition-transform duration-700"
                  />
                  <div className="absolute inset-0 bg-maroon/20 group-hover:bg-transparent transition-colors duration-500 pointer-events-none" />
                </div>
                <div className="mt-auto p-4 text-center bg-maroon text-gold-light font-serif text-lg tracking-wider">
                  Avishkaar 2024
                </div>
              </motion.div>

              <motion.div variants={fadeInUp} className="flex flex-col h-full bg-white/60 dark:bg-white/5 rounded-xl overflow-hidden shadow-lg border border-gold/15 hover:border-gold/30 hover:shadow-xl transition-all duration-400 group">
                <div className="relative aspect-[16/9] overflow-hidden bg-black/5 flex items-center justify-center">
                  <img
                    src="/images/avishkaar_2024.jpg"
                    alt="Avishkaar 2025"
                    width="1024"
                    height="419"
                    loading="lazy"
                    className="w-full h-full object-contain object-center group-hover:scale-110 transition-transform duration-700"
                  />
                  <div className="absolute inset-0 bg-maroon/20 group-hover:bg-transparent transition-colors duration-500 pointer-events-none" />
                </div>
                <div className="mt-auto p-4 text-center bg-maroon text-gold-light font-serif text-lg tracking-wider">
                  Avishkaar 2025
                </div>
              </motion.div>
            </motion.div>

            <motion.div 
              variants={fadeInUp}
              className="text-center bg-white/40 dark:bg-white/5 p-10 rounded-2xl border border-gold/20 shadow-sm max-w-2xl mx-auto transition-colors duration-500"
            >
              <p className="font-sans text-xl font-medium text-maroon/80 dark:text-gold-light/80 mb-6 transition-colors duration-500">To watch full videos, click here:</p>
              <div className="flex flex-col sm:flex-row justify-center items-center gap-6 sm:gap-12">
                <a href="https://youtube.com/@taalsetumusicacademy?si=xKqH1lYomk3qOFR9" target="_blank" rel="noopener noreferrer" className="flex items-center space-x-3 text-maroon dark:text-gold-light hover:text-red-600 dark:hover:text-red-400 transition-colors duration-300 group">
                  <div className="bg-white dark:bg-white/10 p-3 rounded-full shadow-md group-hover:scale-110 transition-transform duration-300">
                    <FaYoutube className="text-3xl text-red-600" />
                  </div>
                  <span className="font-semibold text-lg tracking-wide">YouTube</span>
                </a>
                <a href="https://www.instagram.com/taalsetu?igsh=Z21lMnh2ZmFuOXZ2" target="_blank" rel="noopener noreferrer" className="flex items-center space-x-3 text-maroon dark:text-gold-light hover:text-pink-600 dark:hover:text-pink-400 transition-colors duration-300 group">
                  <div className="bg-white dark:bg-white/10 p-3 rounded-full shadow-md group-hover:scale-110 transition-transform duration-300">
                    <FaInstagram className="text-3xl text-pink-600" />
                  </div>
                  <span className="font-semibold text-lg tracking-wide">Instagram</span>
                </a>
                <a href="https://www.facebook.com/share/14XWpqudVJV/?mibextid=wwXIfr" target="_blank" rel="noopener noreferrer" className="flex items-center space-x-3 text-maroon dark:text-gold-light hover:text-blue-600 dark:hover:text-blue-400 transition-colors duration-300 group">
                  <div className="bg-white dark:bg-white/10 p-3 rounded-full shadow-md group-hover:scale-110 transition-transform duration-300">
                    <FaFacebook className="text-3xl text-blue-600" />
                  </div>
                  <span className="font-semibold text-lg tracking-wide">Facebook</span>
                </a>
              </div>
            </motion.div>
          </div>

          {/* Gold Divider */}
          <div className="gold-divider mb-16" />

          {/* Achievements Grid */}
          <div className="mb-20">
            <div className="text-center mb-10">
              <h3 className="font-serif text-3xl font-bold text-maroon dark:text-gold-light mb-4 transition-colors duration-500">Excellence & Experience</h3>
              <p className="font-sans text-base text-maroon/60 dark:text-gold-light/60 transition-colors duration-500">
                A testament to dedication and global impact
              </p>
            </div>

            <motion.div 
              className="grid md:grid-cols-2 lg:grid-cols-4 gap-6"
              variants={staggerContainer}
            >
              {achievements.map((achievement, index) => (
                <motion.div
                  key={index}
                  variants={fadeInUp}
                  className="bg-white/60 dark:bg-white/5 rounded-xl p-6 text-center border border-gold/10 hover:border-gold/25 transition-all duration-400 hover:shadow-md group"
                >
                  <div className="mb-4 flex justify-center group-hover:scale-110 transition-transform duration-300">
                    {achievement.icon}
                  </div>
                  <h4 className="font-serif text-lg font-bold text-maroon dark:text-gold-light mb-2 transition-colors duration-500">
                    {achievement.title}
                  </h4>
                  <p className="font-sans text-maroon/50 dark:text-gold-light/50 text-sm leading-relaxed transition-colors duration-500">
                    {achievement.description}
                  </p>
                </motion.div>
              ))}
            </motion.div>
          </div>

          {/* Gold Divider */}
          <div className="gold-divider mb-16" />

          {/* Global Impact */}
          <motion.div 
            className="mb-16"
            variants={fadeInUp}
          >
            <div className="bg-maroon rounded-2xl p-8 md:p-12 text-center">
              <FaGlobe className="text-4xl text-gold mx-auto mb-5" />
              <h3 className="font-serif text-3xl font-bold text-gold-light mb-4">Global Reach</h3>
              <p className="font-sans text-gold-light/70 max-w-3xl mx-auto mb-10 text-lg">
                Atharva's expertise has reached a global audience, with students learning tabla under his guidance from around the world.
              </p>

              <div className="grid md:grid-cols-3 gap-8 text-center">
                {[
                  { num: '10+', label: 'Years Teaching' },
                  { num: '500+', label: 'Students Worldwide' },
                  { num: '15+', label: 'Countries Reached' }
                ].map((stat, i) => (
                  <div key={i}>
                    <div className="font-serif text-4xl font-bold text-gold mb-1">{stat.num}</div>
                    <div className="font-sans text-gold-light/60 text-sm tracking-wider uppercase">{stat.label}</div>
                  </div>
                ))}
              </div>
            </div>
          </motion.div>

          {/* Mission Statement */}
          <div className="text-center">
            <h3 className="font-serif text-3xl font-bold text-maroon dark:text-gold-light mb-6 transition-colors duration-500">Our Mission</h3>
            <p className="font-sans text-lg text-maroon/65 dark:text-gold-light/65 leading-relaxed max-w-4xl mx-auto mb-10 transition-colors duration-500">
              <strong className="text-gold-dark dark:text-gold">Taalsetu</strong> offers a structured, interactive, and personalized learning experience that respects tradition while embracing the flexibility of online education. Whether you're a beginner or an advanced learner, Taalsetu is your gateway to mastering the rhythm and soul of Indian classical music.
            </p>

            <motion.div 
              className="grid md:grid-cols-3 gap-8 mt-8"
              variants={staggerContainer}
            >
              {[
                { icon: <FaBookOpen className="text-2xl text-gold" />, title: 'Traditional', desc: 'Rooted in classical Indian music traditions' },
                { icon: <FaChalkboardTeacher className="text-2xl text-gold" />, title: 'Interactive', desc: 'Engaging and personalized learning experience' },
                { icon: <FaGlobe className="text-2xl text-gold" />, title: 'Flexible', desc: 'Online education that fits your schedule' }
              ].map((item, i) => (
                <motion.div key={i} className="text-center group" variants={fadeInUp}>
                  <div className="w-16 h-16 bg-gold/10 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:bg-gold/20 transition-colors duration-300">
                    {item.icon}
                  </div>
                  <h4 className="font-serif font-bold text-maroon dark:text-gold-light mb-2 transition-colors duration-500">{item.title}</h4>
                  <p className="font-sans text-maroon/50 dark:text-gold-light/50 text-sm transition-colors duration-500">{item.desc}</p>
                </motion.div>
              ))}
            </motion.div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default About;